document.addEventListener('DOMContentLoaded', () => {
  'use strict';

  const designer = document.querySelector('[data-me-designer]');
  const openButton = document.querySelector('[data-me-designer-open]');
  const closeButtons = document.querySelectorAll('[data-me-designer-close]');
  const productForm = document.querySelector('.me-main-product__form');

  if (!designer || !openButton || !productForm) {
    return;
  }

  const placementData =
    window.MEPlacementRules?.placements || {};

  const stepOrder = ['design', 'artwork', 'review'];

  const stepButtons = Array.from(
    designer.querySelectorAll('[data-me-step-button]')
  );

  const stepPanels = Array.from(
    designer.querySelectorAll('[data-me-step-panel]')
  );

  const placementInputs = Array.from(
    designer.querySelectorAll('[data-me-placement-checkbox]')
  );

  const selectedPlacementProperty = designer.querySelector(
    '[data-me-selected-placement-property]'
  );

  const placementError = designer.querySelector(
    '[data-me-placement-error]'
  );

  const sizePicker = designer.querySelector('[data-me-size-picker]');
  const sizeProperty = designer.querySelector('[data-me-size-property]');
  const sizeMessage = designer.querySelector('[data-me-size-message]');
  const quantityInput = designer.querySelector('[data-me-quantity-input]');

  const basePriceElement = designer.querySelector('[data-me-price-base]');
  const addOnPriceElement = designer.querySelector('[data-me-price-addons]');
  const unitPriceElement = designer.querySelector('[data-me-price-unit]');
  const totalPriceElement = designer.querySelector('[data-me-price-total]');

  const addOnListElement = designer.querySelector(
    '[data-me-price-addon-list]'
  );

  const primaryAction = designer.querySelector(
    '[data-me-designer-primary-action]'
  );

  const primaryActionLabel = designer.querySelector(
    '[data-me-primary-action-label]'
  );

  const reviewSubmit = designer.querySelector('[data-me-review-submit]');

  const cartStatus = designer.querySelector(
    '[data-me-designer-cart-status]'
  );

  const reviewAlert = designer.querySelector('[data-me-review-alert]');

  const rightsCheckbox = designer.querySelector(
    '[data-me-rights-checkbox]'
  );

  let currentStep = 'design';

  let basePriceCents = Number(
    designer.dataset.meBasePriceCents || 0
  );

  let lastFocusedElement = null;

  function formatMoney(cents) {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format((Number(cents) || 0) / 100);
  }

  function escapeHtml(value) {
    return String(value || '')
      .replaceAll('&', '&amp;')
      .replaceAll('<', '&lt;')
      .replaceAll('>', '&gt;')
      .replaceAll('"', '&quot;')
      .replaceAll("'", '&#039;');
  }

  function createDesignGroupId() {
    if (window.crypto?.randomUUID) {
      return `me-${window.crypto.randomUUID()}`;
    }

    return [
      'me',
      Date.now().toString(36),
      Math.random().toString(36).slice(2, 11)
    ].join('-');
  }

  function getQuantity() {
    const quantity = Number.parseInt(
      quantityInput?.value || '1',
      10
    );

    return Number.isFinite(quantity) && quantity > 0
      ? quantity
      : 1;
  }

  function getSelectedPlacements() {
    return placementInputs
      .filter((input) => input.checked)
      .map((input) => input.value);
  }

  function getPlacementGroup(placement) {
    return placementData[placement]?.group || '';
  }

  function getCurrentColor() {
    const label = designer.querySelector('[data-me-selected-color]');

    const value = String(label?.textContent || '')
      .replace(/^Selected:\s*/i, '')
      .trim();

    if (!value || value.toLowerCase() === 'select a color') {
      return '';
    }

    return value;
  }

  function getCurrentSize() {
    return String(sizeProperty?.value || '').trim();
  }

  function getArtworkSummary() {
    return window.MEArtworkManager?.getSummary() || [];
  }

  function hasRequiredFrontPlacement() {
    return getSelectedPlacements().some(
      (placement) => getPlacementGroup(placement) === 'front'
    );
  }

  function isDesignComplete() {
    return Boolean(
      getCurrentColor() &&
      getCurrentSize() &&
      hasRequiredFrontPlacement()
    );
  }

  function isArtworkComplete() {
    const placements = getSelectedPlacements();
    const manager = window.MEArtworkManager;

    if (!manager || !placements.length) {
      return false;
    }

    return placements.every((placement) =>
      manager.hasAssignment(placement)
    );
  }

  function removeDuplicatePreviewPrompts() {
    designer
      .querySelectorAll('.me-live-preview__empty')
      .forEach((prompt) => prompt.remove());
  }

  function openDesigner() {
    lastFocusedElement = document.activeElement;

    removeDuplicatePreviewPrompts();

    designer.classList.add('is-open');
    designer.setAttribute('aria-hidden', 'false');
    document.body.classList.add('me-designer-open');

    setStep('design');

    designer.querySelector('.me-designer__close')?.focus();
  }

  function closeDesigner() {
    designer.classList.remove('is-open');
    designer.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('me-designer-open');

    lastFocusedElement?.focus?.();
  }

  function setStep(step) {
    if (!stepOrder.includes(step)) {
      return;
    }

    currentStep = step;

    stepPanels.forEach((panel) => {
      const active = panel.dataset.meStepPanel === step;

      panel.classList.toggle('is-active', active);
      panel.hidden = !active;
    });

    updateStepStates();

    if (primaryActionLabel) {
      if (step === 'design') {
        primaryActionLabel.textContent = 'Continue to Artwork';
      } else if (step === 'artwork') {
        primaryActionLabel.textContent = 'Continue to Review';
      } else {
        primaryActionLabel.textContent = 'Add Personalized Item';
      }
    }

    if (step === 'review') {
      renderReview();
    }

    designer
      .querySelector('.me-designer__controls')
      ?.scrollTo({
        top: 0,
        behavior: 'smooth'
      });

    updatePrimaryActionState();
  }

  function updateStepStates() {
    stepButtons.forEach((button) => {
      const step = button.dataset.meStepButton;
      const active = step === currentStep;

      let complete = false;

      if (step === 'design') {
        complete = isDesignComplete();
      }

      if (step === 'artwork') {
        complete = isArtworkComplete();
      }

      if (step === 'review') {
        complete =
          Boolean(rightsCheckbox?.checked) &&
          isDesignComplete() &&
          isArtworkComplete();
      }

      button.classList.toggle('is-active', active);
      button.classList.toggle(
        'is-complete',
        complete && !active
      );

      if (active) {
        button.setAttribute('aria-current', 'step');
      } else {
        button.removeAttribute('aria-current');
      }
    });
  }

  function enforcePlacementConflicts(changedInput) {
    if (changedInput?.checked) {
      const group = getPlacementGroup(changedInput.value);

      placementInputs.forEach((input) => {
        if (input === changedInput) {
          return;
        }

        if (
          group &&
          getPlacementGroup(input.value) === group
        ) {
          input.checked = false;
        }
      });
    }

    if (!hasRequiredFrontPlacement()) {
      const fallback = placementInputs.find(
        (input) => input.value === 'Left Chest'
      );

      if (fallback) {
        fallback.checked = true;
      }
    }

    updatePlacementCards();
    updatePlacementState();
  }

  function updatePlacementCards() {
    placementInputs.forEach((input) => {
      const card = input.closest('[data-me-placement-card]');

      card?.classList.toggle(
        'is-selected',
        input.checked
      );
    });
  }

  function updatePlacementState() {
    const placements = getSelectedPlacements();

    if (selectedPlacementProperty) {
      selectedPlacementProperty.value = placements.join(', ');
    }

    if (placementError) {
      placementError.textContent =
        hasRequiredFrontPlacement()
          ? ''
          : 'Select one included front print location.';
    }

    updatePricing();

    window.dispatchEvent(
      new CustomEvent('me:placements-updated', {
        detail: {
          placements
        }
      })
    );

    updateStepStates();
    updatePrimaryActionState();
  }

  function updatePricing() {
    const placements = getSelectedPlacements();

    const paidAddOnCents = placements.reduce(
      (total, placement) =>
        total +
        Number(
          placementData[placement]?.priceCents || 0
        ),
      0
    );

    const unitPriceCents =
      basePriceCents + paidAddOnCents;

    const totalCents =
      unitPriceCents * getQuantity();

    if (basePriceElement) {
      basePriceElement.textContent =
        formatMoney(basePriceCents);
    }

    if (addOnPriceElement) {
      addOnPriceElement.textContent =
        formatMoney(paidAddOnCents);
    }

    if (unitPriceElement) {
      unitPriceElement.textContent =
        formatMoney(unitPriceCents);
    }

    if (totalPriceElement) {
      totalPriceElement.textContent =
        formatMoney(totalCents);
    }

    if (addOnListElement) {
      addOnListElement.innerHTML = placements
        .map((placement) => {
          const price = Number(
            placementData[placement]?.priceCents || 0
          );

          return `
            <div class="me-price-summary__addon">
              <span>${escapeHtml(placement)}</span>

              <strong>
                ${
                  price === 0
                    ? 'Included'
                    : `+${formatMoney(price)}`
                }
              </strong>
            </div>
          `;
        })
        .join('');
    }

    const reviewTotal = designer.querySelector(
      '[data-me-review-total]'
    );

    if (reviewTotal) {
      reviewTotal.textContent = formatMoney(totalCents);
    }
  }

  function getDesignValidationMessage() {
    if (!getCurrentColor()) {
      return 'Select a garment color to continue.';
    }

    if (!getCurrentSize()) {
      return 'Select a garment size to continue.';
    }

    if (!hasRequiredFrontPlacement()) {
      return 'Select one included front print location.';
    }

    return '';
  }

  function updatePrimaryActionState() {
    if (!primaryAction) {
      return;
    }

    let disabled = false;
    let message = '';

    if (currentStep === 'design') {
      disabled = !isDesignComplete();
      message = getDesignValidationMessage();
    }

    if (currentStep === 'artwork') {
      disabled = !isArtworkComplete();

      if (disabled) {
        message =
          'Complete the customization for every selected location.';
      }
    }

    if (currentStep === 'review') {
      disabled = false;
      message = '';
    }

    primaryAction.disabled = disabled;

    if (cartStatus) {
      cartStatus.textContent = message;
    }
  }

  function validateDesignStep() {
    const message = getDesignValidationMessage();

    if (!message) {
      return true;
    }

    if (cartStatus) {
      cartStatus.textContent = message;
    }

    if (!getCurrentSize()) {
      sizePicker?.scrollIntoView({
        behavior: 'smooth',
        block: 'center'
      });

      if (sizeMessage) {
        sizeMessage.textContent =
          'Select a garment size to continue.';

        sizeMessage.classList.remove('is-complete');
        sizeMessage.classList.add('is-required');
      }
    }

    return false;
  }

  function validateArtworkStep() {
    const placements = getSelectedPlacements();
    const manager = window.MEArtworkManager;

    if (!manager) {
      if (cartStatus) {
        cartStatus.textContent =
          'Artwork controls are still loading.';
      }

      return false;
    }

    const missing = placements.filter(
      (placement) =>
        !manager.hasAssignment(placement)
    );

    if (!missing.length) {
      if (cartStatus) {
        cartStatus.textContent = '';
      }

      return true;
    }

    manager.activate(missing[0]);

    if (cartStatus) {
      cartStatus.textContent =
        `Complete ${missing[0]} before continuing.`;
    }

    return false;
  }

  function renderReview() {
    const placements = getSelectedPlacements();
    const artworkSummary = getArtworkSummary();

    const productTitle = designer.querySelector(
      '[data-me-review-product-title]'
    );

    const color = designer.querySelector(
      '[data-me-review-color]'
    );

    const size = designer.querySelector(
      '[data-me-review-size]'
    );

    const quantity = designer.querySelector(
      '[data-me-review-quantity]'
    );

    const placementList = designer.querySelector(
      '[data-me-review-placements]'
    );

    const artworkList = designer.querySelector(
      '[data-me-review-artwork]'
    );

    if (productTitle) {
      productTitle.textContent =
        designer.dataset.meProductTitle || '';
    }

    if (color) {
      color.textContent = getCurrentColor() || 'Not selected';
    }

    if (size) {
      size.textContent = getCurrentSize() || 'Not selected';
    }

    if (quantity) {
      quantity.textContent = String(getQuantity());
    }

    if (placementList) {
      placementList.innerHTML = placements
        .map((placement) => {
          const price = Number(
            placementData[placement]?.priceCents || 0
          );

          return `
            <div class="me-review-list__item">
              <span>${escapeHtml(placement)}</span>

              <strong>
                ${
                  price === 0
                    ? 'Included'
                    : `+${formatMoney(price)} each`
                }
              </strong>
            </div>
          `;
        })
        .join('');
    }

    if (artworkList) {
      artworkList.innerHTML = placements
        .map((placement) => {
          const assignment = artworkSummary.find(
            (item) => item.placement === placement
          );

          return `
            <div class="me-review-list__item ${
              assignment ? '' : 'is-missing'
            }">
              <span>${escapeHtml(placement)}</span>

              <strong>
                ${
                  assignment
                    ? escapeHtml(assignment.summary)
                    : 'Customization missing'
                }
              </strong>
            </div>
          `;
        })
        .join('');
    }

    updatePricing();
    clearReviewAlert();
    updateStepStates();
  }

  function showReviewAlert(message, type = 'error') {
    if (!reviewAlert) {
      return;
    }

    reviewAlert.hidden = false;
    reviewAlert.className =
      `me-review-alert is-${type}`;
    reviewAlert.textContent = message;

    reviewAlert.scrollIntoView({
      behavior: 'smooth',
      block: 'nearest'
    });
  }

  function clearReviewAlert() {
    if (!reviewAlert) {
      return;
    }

    reviewAlert.hidden = true;
    reviewAlert.textContent = '';
    reviewAlert.className = 'me-review-alert';
  }

  async function rollbackBaseLine(baseLineKey) {
    if (!baseLineKey) {
      return;
    }

    try {
      await fetch('/cart/change.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify({
          id: baseLineKey,
          quantity: 0
        })
      });
    } catch (error) {
      console.warn(
        'Unable to roll back garment after add-on failure.',
        error
      );
    }
  }

  async function addToCart() {
    if (!validateDesignStep()) {
      setStep('design');
      return;
    }

    if (!validateArtworkStep()) {
      setStep('artwork');
      return;
    }

    setStep('review');

    if (!rightsCheckbox?.checked) {
      showReviewAlert(
        'Confirm the copyright and artwork authorization before adding this item to cart.'
      );

      rightsCheckbox?.focus();
      updateStepStates();
      return;
    }

    const placements = getSelectedPlacements();
    const quantity = getQuantity();
    const artworkSummary = getArtworkSummary();
    const designGroupId = createDesignGroupId();

    const paidPlacements = placements.filter(
      (placement) =>
        Number(
          placementData[placement]?.priceCents || 0
        ) > 0
    );

    const missingVariant = paidPlacements.find(
      (placement) =>
        !Number(
          placementData[placement]?.variantId
        )
    );

    if (missingVariant) {
      showReviewAlert(
        `${missingVariant} is missing its Shopify add-on variant ID.`
      );

      return;
    }

    clearReviewAlert();

    if (reviewSubmit) {
      reviewSubmit.disabled = true;

      const label = reviewSubmit.querySelector(
        'span:first-child'
      );

      if (label) {
        label.textContent = 'Adding to Cart…';
      }
    }

    if (primaryAction) {
      primaryAction.disabled = true;
    }

    let baseLineKey = '';

    try {
      const baseFormData = new FormData(productForm);

      baseFormData.set('quantity', String(quantity));

      baseFormData.set(
        'properties[Garment Size]',
        getCurrentSize()
      );

      baseFormData.set(
        'properties[Garment Color]',
        getCurrentColor()
      );

      baseFormData.set(
        'properties[Selected Print Locations]',
        placements.join(', ')
      );

      baseFormData.set(
        'properties[Artwork File Count]',
        String(artworkSummary.length)
      );

      baseFormData.set(
        'properties[_ME Design Group]',
        designGroupId
      );

      baseFormData.set(
        'properties[_ME Line Type]',
        'garment'
      );

      baseFormData.set(
        'properties[_ME Designer Version]',
        '2.0'
      );

      window.MEArtworkManager?.appendProperties(
        baseFormData
      );

      const root =
        window.Shopify?.routes?.root || '/';

      const baseResponse = await fetch(
        `${root}cart/add.js`,
        {
          method: 'POST',
          body: baseFormData,
          headers: {
            Accept: 'application/json'
          }
        }
      );

      if (!baseResponse.ok) {
        const responseData = await baseResponse
          .json()
          .catch(() => ({}));

        throw new Error(
          responseData.description ||
          'The personalized garment could not be added.'
        );
      }

      const baseItem = await baseResponse.json();
      baseLineKey = baseItem?.key || '';

      const addOnItems = paidPlacements.map(
        (placement) => ({
          id: Number(
            placementData[placement].variantId
          ),

          quantity,

          properties: {
            Customization: placement,

            'For Product':
              designer.dataset.meProductTitle,

            'Garment Color': getCurrentColor(),

            'Garment Size': getCurrentSize(),

            '_ME Design Group': designGroupId,

            '_ME Line Type': 'addon',

            '_ME Parent Line Key': baseLineKey,

            '_ME Parent Variant':
              productForm.querySelector(
                '[data-me-variant-id]'
              )?.value || ''
          }
        })
      );

      if (addOnItems.length) {
        const addOnResponse = await fetch(
          `${root}cart/add.js`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/json'
            },
            body: JSON.stringify({
              items: addOnItems
            })
          }
        );

        if (!addOnResponse.ok) {
          const responseData = await addOnResponse
            .json()
            .catch(() => ({}));

          await rollbackBaseLine(baseLineKey);

          throw new Error(
            responseData.description ||
            'The paid customization add-ons could not be added.'
          );
        }
      }

      window.location.assign(`${root}cart`);
    } catch (error) {
      showReviewAlert(
        error.message ||
        'Something went wrong while adding the order.'
      );

      if (reviewSubmit) {
        reviewSubmit.disabled = false;

        const label = reviewSubmit.querySelector(
          'span:first-child'
        );

        if (label) {
          label.textContent =
            'Add Personalized Item';
        }
      }

      updatePrimaryActionState();
    }
  }

  function handlePrimaryAction() {
    if (currentStep === 'design') {
      if (!validateDesignStep()) {
        return;
      }

      setStep('artwork');
      return;
    }

    if (currentStep === 'artwork') {
      if (!validateArtworkStep()) {
        return;
      }

      setStep('review');
      return;
    }

    addToCart();
  }

  openButton.addEventListener('click', openDesigner);

  closeButtons.forEach((button) => {
    button.addEventListener('click', closeDesigner);
  });

  stepButtons.forEach((button) => {
    button.addEventListener('click', () => {
      const targetStep = button.dataset.meStepButton;

      if (
        targetStep === 'artwork' &&
        !validateDesignStep()
      ) {
        return;
      }

      if (targetStep === 'review') {
        if (!validateDesignStep()) {
          return;
        }

        if (!validateArtworkStep()) {
          setStep('artwork');
          return;
        }
      }

      setStep(targetStep);
    });
  });

  placementInputs.forEach((input) => {
    input.addEventListener('change', () => {
      enforcePlacementConflicts(input);
    });
  });

  primaryAction?.addEventListener(
    'click',
    handlePrimaryAction
  );

  reviewSubmit?.addEventListener(
    'click',
    (event) => {
      event.preventDefault();
      addToCart();
    }
  );

  rightsCheckbox?.addEventListener(
    'change',
    () => {
      updateStepStates();
      clearReviewAlert();
    }
  );

  quantityInput?.addEventListener(
    'input',
    () => {
      updatePricing();

      if (currentStep === 'review') {
        renderReview();
      }
    }
  );

  quantityInput?.addEventListener(
    'change',
    updatePricing
  );

  window.addEventListener(
    'me:designer-variant-changed',
    (event) => {
      const newPrice = Number(
        event.detail?.priceCents
      );

      if (Number.isFinite(newPrice)) {
        basePriceCents = newPrice;
      }

      updatePricing();
      updateStepStates();
      updatePrimaryActionState();
    }
  );

  window.addEventListener(
    'me:variant-selection-state',
    () => {
      updateStepStates();
      updatePrimaryActionState();

      if (currentStep === 'review') {
        renderReview();
      }
    }
  );

  window.addEventListener(
    'me:designer-color-changed',
    () => {
      updateStepStates();
      updatePrimaryActionState();
    }
  );

  window.addEventListener(
    'me:artwork-updated',
    () => {
      updateStepStates();
      updatePrimaryActionState();

      if (currentStep === 'review') {
        renderReview();
      }
    }
  );

  productForm.addEventListener(
    'submit',
    (event) => {
      event.preventDefault();

      if (currentStep !== 'review') {
        handlePrimaryAction();
        return;
      }

      addToCart();
    }
  );

  document.addEventListener(
    'keydown',
    (event) => {
      if (
        event.key === 'Escape' &&
        designer.classList.contains('is-open')
      ) {
        closeDesigner();
      }
    }
  );

  removeDuplicatePreviewPrompts();
  updatePlacementCards();
  updatePlacementState();
  updatePricing();
  setStep('design');
});